import type { SupportUserStatus } from "@src/features/tenants";

/** `"pending"` means render nothing yet — never deny, see {@link resolveClipCompilationAccess}. */
export type ClipCompilationAccess = "pending" | "granted" | "denied";

export interface ClipCompilationAccessInputs {
  readonly isEnabled: boolean;
  readonly supportStatus: SupportUserStatus;
}

/**
 * Support staff reach the feature whatever the flag says; everyone else is
 * gated on it.
 *
 * The flag is checked first so an unsettled support lookup cannot hold up the
 * common case — once the flag is on, who the caller is no longer matters. Only
 * a flag that is off makes the role load-bearing, and there denying before it
 * settles would bounce a legitimate support user off a cold deep-link.
 *
 * The flag gates this UI only — deliberately, unlike timelapse, which also
 * checks its flag server-side. The compilation endpoints do not, so a customer
 * with the flag off can still read their own compilations through the API.
 * That is a rollout control, not an entitlement boundary; treat it as one and
 * this gate is not where the difference gets fixed.
 *
 * Split from the hook so it can be unit tested without the React Native module
 * graph, mirroring `resolveTestSignalsAccess`.
 */
export function resolveClipCompilationAccess({
  isEnabled,
  supportStatus,
}: ClipCompilationAccessInputs): ClipCompilationAccess {
  if (isEnabled) return "granted";
  if (supportStatus === "pending") return "pending";
  return supportStatus === "granted" ? "granted" : "denied";
}
